import React from "react";
import {IconVehicle} from "../../../../../../components/buttons/IconButtons";
import {CustomTextField} from "../../../../../../components/CustomTextField";
import {isNotEmpty} from "../../../../../../Apis/functions";
import {getTransportFieldLength} from "../../../Model/Model";

/**
 * Fields of the transport that returns the crates and pallets.
 *
 * The whole transport is read at the gate, so none of its fields is required
 * for the requester and what is left blank is completed by whoever receives
 * the vehicle.
 */
export const TRANSPORT_FIELDS = [
    {
        id: 'txt_cap_carrier_company',
        name: 'carrierCompany',
        trans: 'carrier_company',
        placeholder: 'enter_carrier_company_name',
        className: 'col-12'
    },
    {
        id: 'txt_cap_tractor',
        name: 'tractor',
        trans: 'tractor',
        placeholder: 'enter_plate_or_unit_number',
        className: 'col-sm-12 col-md-6'
    },
    {
        id: 'txt_cap_trailer',
        name: 'trailer',
        trans: 'trailer',
        placeholder: 'enter_plate_or_unit_number',
        className: 'col-sm-12 col-md-6'
    },
    {
        id: 'txt_cap_driver_s_name',
        name: 'driversName',
        trans: 'driver_s_name',
        placeholder: 'enter_driver_s_name',
        className: 'col-sm-12 col-md-6'
    },
    {
        id: 'txt_cap_seal_number',
        name: 'sealNumber',
        trans: 'seal_number',
        placeholder: 'enter_seal_number',
        className: 'col-sm-12 col-md-6'
    }
];

/**
 * Fields of the transport as the guard captures them at the gate.
 *
 * The whole transport is optional for the requester, so which fields belong to
 * the guard is told by the folio itself and not by the form: the value the
 * requester registered is only read, and the one that was left blank is
 * required, because the movement is not closed without the vehicle complete.
 *
 * A block that is only read displays the whole vehicle with the mark of the
 * value the gate was asked for, because what it says of the unit is read back
 * and no longer written.
 *
 * @param {Object} item - Folio the movement belongs to.
 * @param {boolean} [readOnly] - Whether the block is only read.
 *
 * @returns {Array} Fields of the transport as the gate captures them.
 */
export const getGuardTransportFields = (item, readOnly = false) => TRANSPORT_FIELDS.map((field) => {
    const registered = isNotEmpty(item?.[field.name]);

    return {...field, disabled: readOnly || registered, required: readOnly || !registered};
});

/**
 * @Author: Karen Yunuen Pozos Arias
 * @JobTitle: Full Stack Developer - IBL
 * @Email: kpozos@ibl.mx
 *
 * @description
 * Renders the transport that returns the crates and pallets. Whoever captures
 * the form decides which fields are theirs, so the fields it displays, and
 * whether each one is required or only read, come from the configuration it
 * receives.
 *
 * @param {Function} t - Translation function.
 * @param {Object} materialRequest - Current form information.
 * @param {Function} handleChange - Change handler of the form.
 * @param {boolean} emptyFields - Indicates whether the required fields must be highlighted.
 * @param {Array} fields - Configuration of the fields being captured.
 * @param {string} contentClassName - Spacing the fields are read with inside the block.
 */
const CratesAndPalletsTransportSection = ({
    t,
    materialRequest,
    handleChange,
    emptyFields,
    fields = TRANSPORT_FIELDS,
    disabled = false,
    contentClassName = 'row py-3'
}) => {

    return (
        <div className="border-full-shades-300 background-general-00 border-radius-8px my-3">
            <div className={"d-flex align-items-center p-2"}>
                <div className={"mr-2 d-flex align-items-center"}><IconVehicle/></div>
                <p className={"fs-14px ibl-font-w-600"}>{t("transport")}</p>
            </div>
            <div className={"border-bottom-shades-300"}/>

            <div className={contentClassName}>
                {fields.map(({id, name, trans, placeholder, className, required = false, disabled: fieldDisabled = false}) =>
                    <div className={className} key={name}>
                        <CustomTextField
                            id={id}
                            length={getTransportFieldLength(name)}
                            name={name}
                            value={materialRequest?.[name]}
                            trans={trans}
                            type={'text'}
                            disabled={disabled || fieldDisabled}
                            onChange={handleChange}
                            emptyFields={emptyFields && !disabled && required}
                            required={required}
                            placeholder={t(placeholder)}
                        />
                    </div>
                )}
            </div>
        </div>
    );
};

export default CratesAndPalletsTransportSection;
